import React, { useCallback, useEffect, useMemo } from "react";

import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, InputAdornment, MenuItem, TextField } from "@mui/material";
import { useActionHook, useCannotMoveToStageHook, useModals } from "hooks";
import {
  useDealActions,
  useDealIdSelector,
  useDealStageIdSelector,
  useDealStatusesSelector,
  useDealStatusSelector,
  useOpportunityIdSelector,
  usePickUserPermissions
} from "store";
import { DealStage, IStatus, UserPermissionType } from "store/types";

const DEFAULT_COLOR: string = "#0a3142";

export const Status: React.FC = () => {
  const dealStatus = useDealStatusSelector();
  const dealId = useDealIdSelector();
  const dealStageId = useDealStageIdSelector();
  const statuses = useDealStatusesSelector();
  const { updateDealAction, getDealStagesAction } = useDealActions();
  const opportunityId = useOpportunityIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const { errorModal } = useModals();
  const { showCannotMoveToStageModal } = useCannotMoveToStageHook();

  const { data = [], action } = useActionHook(getDealStagesAction<false>);

  useEffect(() => {
    action({ with_deals: false });
  }, []);

  const options: Array<Pick<DealStage, "id" | "title" | "status_id" | "is_system"> & Pick<IStatus, "color">> =
    useMemo(() => {
      return data.map(({ id, title, status_id, is_system }) => {
        const color = statuses.find((status) => status.id === status_id)?.color || DEFAULT_COLOR;
        return { id, title, color, status_id, is_system };
      });
    }, [data, statuses]);

  const onChange = useCallback(
    async (stageId: number) => {
      if (stageId === dealStageId || !dealId) return;
      try {
        await updateDealAction({ dealId, deal_stage_id: stageId });
      } catch (error: unknown) {
        const err = error as {
          status?: number;
          errors?: { code?: string; missing_fields?: Array<{ id: number; name: string }>; target_status?: string; target_stage_id?: number };
          response?: { status: number; data?: { errors?: { code?: string; missing_fields?: Array<{ id: number; name: string }>; target_status?: string; target_stage_id?: number } } };
        };
        const status = err.status ?? err.response?.status;
        const errors = err.errors ?? err.response?.data?.errors;
        if (status === 422 && errors?.code === "status_transition_validation_failed" && errors?.missing_fields?.length) {
          const targetStatusName = errors.target_status ?? options.find((o) => o.id === stageId)?.title ?? "this stage";
          showCannotMoveToStageModal({
            dealId,
            targetStageName: targetStatusName,
            targetStageId: errors.target_stage_id ?? stageId,
            missingFields: errors.missing_fields
          });
        } else {
          let msg = (err as { message?: string }).message ?? "Something went wrong";
          if (status === 422 && errors && typeof errors === "object") {
            const first = Object.values(errors).flat().find((m): m is string => typeof m === "string");
            if (first) msg = first;
          }
          errorModal({ msg });
        }
      }
    },
    [dealId, dealStageId, options, updateDealAction, showCannotMoveToStageModal, errorModal]
  );

  const isGreen = dealStatus?.name === "Invested" || dealStatus?.id === 4;

  const selectedOptionColor = useMemo(() => {
    return options.find(({ id }) => id === dealStageId)?.color || DEFAULT_COLOR;
  }, [dealStageId, options]);

  if (!dealStageId || !options.length) return null;

  return (
    <Box sx={{ minWidth: 150 }}>
      <TextField
        dir="auto"
        disabled={dealStatus?.id === opportunityId || noAccessPermission || isViewerPermission}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <TripOriginIcon sx={{ width: "15px", height: "15px", color: selectedOptionColor }} />
            </InputAdornment>
          )
        }}
        sx={{
          padding: 0,
          outline: 0,
          ".MuiInputBase-root": {
            backgroundColor: isGreen ? "#caeadb" : "transparent"
          },
          fieldset: { borderWidth: "1px !important" },

          ".MuiSelect-select": {
            padding: "0 5px 0 0 !important",
            color: selectedOptionColor,
            borderRadius: "20px",
            borderColor: "#bac7ce",
            outline: 0,
            fontSize: "15px !important",
            svg: { display: "none" }
          },
          ".MuiInputBase-root .MuiSvgIcon-root": { position: "relative" },
          ".MuiOutlinedInput-root": {
            borderRadius: "20px",
            borderColor: "#bac7ce",
            outline: 0
          },
          ".MuiOutlinedInput-notchedOutline": { borderRadius: "20px", padding: 0, outline: 0 },
          "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderRadius: "20px",
            borderColor: "#bac7ce"
          },
          "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderRadius: "20px",
            borderColor: "#bac7ce",
            outline: 0
          },
          ...(dealStatus?.id === opportunityId && {
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "initial"
            }
          })
        }}
        select
        variant="outlined"
        onChange={(e) => onChange(Number(e.target.value))}
        value={dealStageId}
      >
        {options.map(({ id, title, color, status_id, is_system }) => {
          return (
            <MenuItem
              disabled={is_system}
              sx={{
                color: `${color} !important`,
                gap: "5px",
                ...(status_id === opportunityId &&
                  dealStatus?.id !== opportunityId && {
                    display: "none"
                  })
              }}
              key={id}
              value={id}
            >
              <TripOriginIcon sx={{ width: "15px", height: "15px", color: color }} />
              {title}
            </MenuItem>
          );
        })}
      </TextField>
    </Box>
  );
};
